import { Decimal } from 'decimal.js';
import { assetUtils, numberUtils } from '@zen/common-utils';
import { DefaultTickers, ZenJsUtils } from '@zen/common-app-parts';

/**
 * Calls the right setter
 * @param {Object} params
 * @param {Object} params.PropsSettersMap - an object which maps a prop name to a setter function
 * @param {string} params.prop - the prop name
 * @param {any} params.value - the value to set
 * @param {Object} params.draft - the state object
 */
export function setter({ PropsSettersMap, prop, value, draft } = {}) {
  if (!PropsSettersMap[prop]) {
    throw new Error(`Unhandled prop setter for prop ${prop}`);
  }

  PropsSettersMap[prop](value, draft);
}

export function setOracle(value, draft) {
  draft.oracle = value;
}
export function setPosition(value, draft) {
  draft.position.value = value;
  draft.position.valid = ['Bull', 'Bear'].includes(value);
}
export function setTicker(value, draft) {
  draft.ticker.value = value;
  draft.ticker.valid = DefaultTickers.includes(value);
}
export function setPrice(value, draft) {
  _setPrice({ draft, prop: 'price', value, decimals: 2 });
}
export function setDate(value, draft) {
  draft.date.value = value;
  draft.date.valid = value ? !!value.valueOf() : false;
}
export function setResult(value, draft) {
  const { error, messageBody, assetInfo } = value;
  draft.result.error = error || '';
  draft.result.messageBody = messageBody || '';
  draft.result.assetInfo = assetInfo || '';
}
export function setCollateralAsset(value, draft) {
  _setAsset({ prop: 'collateralAsset', draft, value });
}
export function setCollateralAmount(value, draft) {
  _setAmount({ draft, prop: 'collateralAmount', value });
}
export function setAsset1(value, draft) {
  _setAsset({ prop: 'asset1', draft, value });
}
export function setAmount1(value, draft) {
  _setAmount({ draft, prop: 'amount1', value });
}
export function setAsset2(value, draft) {
  _setAsset({ prop: 'asset2', draft, value });
}
export function setAmount2(value, draft) {
  _setAmount({ draft, prop: 'amount2', value });
}
export function setFetchPhase(value, draft) {
  draft.fetchPhase = value;
}

/**
 * Helper to set an asset field
 */
function _setAsset({ prop, value, draft }) {
  draft[prop].value = value;
  draft[prop].valid = ZenJsUtils.validateAsset(assetUtils.getAssetFullId(value));
}

/**
 * Helper to set an amount field which uses ValueDisplay
 */
function _setAmount({ prop, value, draft, decimals }) {
  const newAmount = draft[prop].value.onDisplayChange(
    numberUtils.truncateToDecimalPlaces(value, decimals)
  );
  draft[prop].value = newAmount;
  draft[prop].valid = new Decimal(newAmount.safeValue).gte(0.00000001);
}

/**
 * Helper to set the price field which uses ValueDisplay
 */
function _setPrice({ prop, value, draft, decimals }) {
  const newAmount = draft[prop].value.onDisplayChange(
    numberUtils.truncateToDecimalPlaces(value, decimals)
  );
  const priceValid = new Decimal(newAmount.safeValue)
    .times(100)
    .toString()
    .match('^[0-9]{1,21}$|^[0-9]{1,21}[.]{1}[0-9]{0,2}$');
  draft[prop].value = newAmount;
  draft[prop].valid = priceValid === null ? false : new Decimal(newAmount.safeValue).gte(0.01);
}
